import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Grid,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Switch,
	FormControlLabel,
	Box,
	CircularProgress,
	Alert,
	Typography,
	Slider,
} from "@mui/material";
import {
	SettingManagement,
	CreateSettingPayload,
	UpdateSettingPayload,
} from "../../../domain/settings.interface";

interface SettingsModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (data: CreateSettingPayload | UpdateSettingPayload) => Promise<void>;
	editingSetting?: SettingManagement | null;
	isAccountView?: boolean;
	selectedAccountName?: string;
}

interface FormData {
	product: string;
	application: "mobile" | "backoffice" | "platform";
	module: string;
	section: string;
	name: string;
	description: string;
	type: "string" | "number" | "boolean" | "object" | "array";
	default: string;
	active: boolean;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
	open,
	onClose,
	onSave,
	editingSetting,
	isAccountView = false,
	selectedAccountName,
}) => {
	const [isLoading, setIsLoading] = React.useState(false);

	// Determine if we're editing an account override
	const isEditingAccountOverride =
		isAccountView && editingSetting?.accountOverride;

	// Helper function to safely display default value
	const formatDefaultValue = (value: string): string => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			product: process.env.APP_APPLICATION_ID || "sims",
			application: "backoffice",
			module: "",
			section: "",
			name: "",
			description: "",
			type: "string",
			default: "",
			active: true,
		},
	});

	const typeValue = watch("type");

	// Populate form when editing
	useEffect(() => {
		if (editingSetting && open) {
			// If editing an account override, use the override value
			const valueToEdit = isEditingAccountOverride
				? editingSetting.accountOverride?.value
				: editingSetting.default;

			reset({
				product: editingSetting.product,
				application: editingSetting.application,
				module: editingSetting.module,
				section: editingSetting.section,
				name: editingSetting.name,
				description: editingSetting.description,
				type: editingSetting.type,
				default:
					typeof valueToEdit === "object"
						? JSON.stringify(valueToEdit)
						: String(valueToEdit),
				active: isEditingAccountOverride
					? (editingSetting.accountOverride?.active ?? true)
					: editingSetting.active,
			});
		} else if (!open) {
			reset({
				product: process.env.APP_APPLICATION_ID || "sims",
				application: "backoffice",
				module: "",
				section: "",
				name: "",
				description: "",
				type: "string",
				default: "",
				active: true,
			});
		}
	}, [editingSetting, open, reset, isEditingAccountOverride]);

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		try {
			// Send default value as string - backend will handle conversions
			const payload = {
				product: data.product,
				application: data.application,
				module: data.module,
				section: data.section,
				name: data.name,
				description: data.description,
				type: data.type,
				default: data.default, // Always send as string
				active: data.active,
			};

			await onSave(payload);
		} catch (error) {
			// Error handled by React Query
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{editingSetting
					? isEditingAccountOverride
						? `Edit Account Override - ${selectedAccountName}`
						: `Edit Setting${isAccountView ? ` - Create Override for ${selectedAccountName}` : ""}`
					: isAccountView
						? `Create Account Override - ${selectedAccountName}`
						: "Create New Setting"}
			</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						minHeight={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box component="form" sx={{ mt: 2 }}>
						{isAccountView && (
							<Alert severity="info" sx={{ mb: 2 }}>
								{isEditingAccountOverride
									? `You are editing the account-specific override for ${selectedAccountName}. This will only affect this account.`
									: `You are creating an account-specific override for ${selectedAccountName}. This will override the global default value for this account only.`}
							</Alert>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Controller
									name="application"
									control={control}
									rules={{ required: "Application is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.application}
											disabled={isAccountView}
										>
											<InputLabel>Application</InputLabel>
											<Select {...field} label="Application">
												<MenuItem value="mobile">Mobile</MenuItem>
												<MenuItem value="backoffice">Backoffice</MenuItem>
												<MenuItem value="platform">Platform</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="module"
									control={control}
									rules={{ required: "Module is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Module"
											fullWidth
											error={!!errors.module}
											helperText={errors.module?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="section"
									control={control}
									rules={{ required: "Section is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Section"
											fullWidth
											error={!!errors.section}
											helperText={errors.section?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="name"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Name"
											fullWidth
											error={!!errors.name}
											helperText={errors.name?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								<Controller
									name="description"
									control={control}
									rules={{ required: "Description is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Description"
											fullWidth
											multiline
											rows={2}
											error={!!errors.description}
											helperText={errors.description?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="type"
									control={control}
									rules={{ required: "Type is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.type}
											disabled={isAccountView}
										>
											<InputLabel>Type</InputLabel>
											<Select {...field} label="Type">
												<MenuItem value="string">String</MenuItem>
												<MenuItem value="number">Number</MenuItem>
												<MenuItem value="boolean">Boolean</MenuItem>
												<MenuItem value="object">Object</MenuItem>
												<MenuItem value="array">Array</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="active"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={<Switch {...field} checked={field.value} />}
											label="Active"
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								{/* Custom editors for specific settings, raw JSON for others */}
								{(() => {
									const settingName = editingSetting?.name || watch("name");
									if (settingName === "themeColors") {
										return <ThemeColorPicker control={control} watch={watch} />;
									}
									if (settingName === "certificateConfig") {
										return (
											<CertificateConfigPicker
												control={control}
												watch={watch}
											/>
										);
									}
									if (settingName === "beneficiaryTerms") {
										return (
											<BeneficiaryTermsPicker control={control} watch={watch} />
										);
									}
									return (
										<Controller
											name="default"
											control={control}
											rules={{ required: "Default value is required" }}
											render={({ field }) => (
												<TextField
													{...field}
													label={
														isAccountView
															? "Override Value (Account-Specific)"
															: "Default Value"
													}
													fullWidth
													multiline={
														typeValue === "object" || typeValue === "array"
													}
													rows={
														typeValue === "object" || typeValue === "array"
															? 4
															: 1
													}
													error={!!errors.default}
													helperText={
														errors.default?.message ||
														(typeValue === "object" || typeValue === "array"
															? "Enter valid JSON"
															: isAccountView && editingSetting
																? `Global default: ${formatDefaultValue(editingSetting.default)}`
																: "")
													}
												/>
											)}
										/>
									);
								})()}
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{editingSetting ? "Update" : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Inline color swatch with native color picker + hex text field.
 */
// Parses a hex or rgba(r,g,b,a) string into { hex, opacityPct }.
function parseColorValue(value: string): { hex: string; opacityPct: number } {
	const rgba = value.match(
		/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\s*\)/,
	);
	if (rgba) {
		const hex = `#${[rgba[1], rgba[2], rgba[3]]
			.map((n) => parseInt(n).toString(16).padStart(2, "0"))
			.join("")}`;
		return { hex, opacityPct: Math.round(parseFloat(rgba[4] ?? "1") * 100) };
	}
	if (/^#[0-9A-Fa-f]{6}$/.test(value)) return { hex: value, opacityPct: 100 };
	return { hex: "#FFFFFF", opacityPct: 100 };
}

// Converts hex + opacity% to a CSS color string.
// Returns hex when opacity is 100%, rgba(...) otherwise.
function buildColorValue(hex: string, opacityPct: number): string {
	if (opacityPct >= 100) return hex;
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${parseFloat((opacityPct / 100).toFixed(2))})`;
}

// Color picker with an opacity slider. Emits hex (100%) or rgba(<100%).
function ColorFieldWithOpacity({
	label,
	hexValue,
	opacityPct,
	placeholder,
	caption,
	onHexChange,
	onOpacityChange,
}: {
	label: string;
	hexValue: string;
	opacityPct: number;
	placeholder: string;
	caption: string;
	onHexChange: (hex: string) => void;
	onOpacityChange: (opacity: number) => void;
}) {
	const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(hexValue);
	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1}>
				<input
					type="color"
					value={isValidHex ? hexValue : "#FFFFFF"}
					onChange={(e) => onHexChange(e.target.value)}
					style={{
						width: 40,
						height: 40,
						border: "1px solid #ccc",
						borderRadius: 8,
						cursor: "pointer",
						padding: 2,
						flexShrink: 0,
					}}
				/>
				<TextField
					label={label}
					value={hexValue}
					onChange={(e) => onHexChange(e.target.value)}
					size="small"
					sx={{ flex: 1 }}
					placeholder={placeholder}
				/>
				<TextField
					label="Opacity %"
					type="number"
					value={opacityPct}
					onChange={(e) => {
						const val = Math.min(
							100,
							Math.max(0, parseInt(e.target.value) || 0),
						);
						onOpacityChange(val);
					}}
					inputProps={{ min: 0, max: 100, step: 1 }}
					size="small"
					sx={{ width: 90 }}
				/>
			</Box>
			<Box sx={{ px: 0.5, mt: 0.5 }}>
				<Slider
					value={opacityPct}
					min={0}
					max={100}
					step={1}
					onChange={(_e, val) => onOpacityChange(val as number)}
					size="small"
				/>
			</Box>
			<Typography variant="caption" color="text.secondary">
				{caption}
				{opacityPct < 100 && isValidHex
					? ` — ${buildColorValue(hexValue, opacityPct)}`
					: ""}
			</Typography>
		</Box>
	);
}

function ColorField({
	label,
	value,
	placeholder,
	onChange,
}: {
	label: string;
	value: string;
	placeholder: string;
	onChange: (hex: string) => void;
}) {
	return (
		<Box display="flex" alignItems="center" gap={2}>
			<input
				type="color"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				style={{
					width: 48,
					height: 48,
					border: "1px solid #ccc",
					borderRadius: 8,
					cursor: "pointer",
					padding: 2,
				}}
			/>
			<TextField
				label={label}
				value={value}
				onChange={(e) => {
					const val = e.target.value;
					onChange(val);
				}}
				size="small"
				fullWidth
				placeholder={placeholder}
			/>
		</Box>
	);
}

function getSwatchTextColor(hex: string): string {
	try {
		return parseInt(hex.replace("#", ""), 16) > 0x7fffff ? "#000" : "#FFF";
	} catch {
		return "#FFF";
	}
}

/**
 * Color picker sub-component for the themeColors setting.
 * Shows individual color pickers for primary, secondary, surface + font family.
 *
 * Based on B2D brand spec:
 *  - Primary (#276048) -> navigation, menu, buttons
 *  - Secondary (#70A815) -> secondary actions, badges
 *  - Surface (#DEE7E7) -> backgrounds, cards
 *  - Font: Roboto (Regular, Medium, Semibold, Bold)
 */

interface ThemeEmitState {
	primary: string;
	secondary: string;
	tertiary: string;
	surfaceTint: string;
	surface: string;
	surfLight: string;
	surfLightOp: number;
	surfDark: string;
	surfDarkOp: number;
	primDark: string;
	primDarkOp: number;
	secLight: string;
	secLightOp: number;
	outMain: string;
	outMainOp: number;
	outDark: string;
	outDarkOp: number;
	txtMainDk2: string;
	txtMainDk2Op: number;
	txtMainMn2: string;
	txtMainMn2Op: number;
	txtSecMain: string;
	txtSecMainOp: number;
	font: string;
}

function ThemeColorPicker({
	control,
	watch,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
}) {
	const currentValue = watch("default");

	// Base colors
	const [primaryColor, setPrimaryColor] = useState("#52197B");
	const [secondaryColor, setSecondaryColor] = useState("#4CB7A4");
	// Tertiary brand accent (JANUS lilac); the theme pipeline falls back to this
	// same default (#7034E7) when the setting omits tertiaryColor.
	const [tertiaryColor, setTertiaryColor] = useState("#7034E7");
	// Tinted-surface role (JANUS blue-100); the theme pipeline falls back to this
	// default (#DFF5FF) when the setting omits surfaceTint.
	const [surfaceTint, setSurfaceTint] = useState("#DFF5FF");
	const [surfaceColor, setSurfaceColor] = useState("#F7F4F9");

	// Surface light/dark with opacity
	const [surfaceLight, setSurfaceLight] = useState("#FFFFFF");
	const [surfaceLightOpacity, setSurfaceLightOpacity] = useState(100);
	const [surfaceDark, setSurfaceDark] = useState("#E9D0F4");
	const [surfaceDarkOpacity, setSurfaceDarkOpacity] = useState(100);

	// Advanced overrides (empty string = not set, use derived value)
	const [primDark, setPrimDark] = useState("");
	const [primDarkOp, setPrimDarkOp] = useState(100);
	const [secLight, setSecLight] = useState("");
	const [secLightOp, setSecLightOp] = useState(100);
	const [outMain, setOutMain] = useState("");
	const [outMainOp, setOutMainOp] = useState(100);
	const [outDark, setOutDark] = useState("");
	const [outDarkOp, setOutDarkOp] = useState(100);
	const [txtMainDk2, setTxtMainDk2] = useState("");
	const [txtMainDk2Op, setTxtMainDk2Op] = useState(100);
	const [txtMainMn2, setTxtMainMn2] = useState("");
	const [txtMainMn2Op, setTxtMainMn2Op] = useState(100);
	const [txtSecMain, setTxtSecMain] = useState("");
	const [txtSecMainOp, setTxtSecMainOp] = useState(100);

	const [fontFamily, setFontFamily] = useState("Roboto");

	// Parse current JSON value into individual fields
	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (parsed?.primaryColor) setPrimaryColor(parsed.primaryColor);
			if (parsed?.secondaryColor) setSecondaryColor(parsed.secondaryColor);
			if (parsed?.tertiaryColor) setTertiaryColor(parsed.tertiaryColor);
			if (parsed?.surfaceTint) setSurfaceTint(parsed.surfaceTint);
			if (parsed?.surfaceColor) setSurfaceColor(parsed.surfaceColor);
			if (parsed?.surfaceLight) {
				const { hex, opacityPct } = parseColorValue(parsed.surfaceLight);
				setSurfaceLight(hex);
				setSurfaceLightOpacity(opacityPct);
			}
			if (parsed?.surfaceDark) {
				const { hex, opacityPct } = parseColorValue(parsed.surfaceDark);
				setSurfaceDark(hex);
				setSurfaceDarkOpacity(opacityPct);
			}
			if (parsed?.primaryDark) {
				const { hex, opacityPct } = parseColorValue(parsed.primaryDark);
				setPrimDark(hex);
				setPrimDarkOp(opacityPct);
			}
			if (parsed?.secondaryLight) {
				const { hex, opacityPct } = parseColorValue(parsed.secondaryLight);
				setSecLight(hex);
				setSecLightOp(opacityPct);
			}
			if (parsed?.outlineMain) {
				const { hex, opacityPct } = parseColorValue(parsed.outlineMain);
				setOutMain(hex);
				setOutMainOp(opacityPct);
			}
			if (parsed?.outlineDark) {
				const { hex, opacityPct } = parseColorValue(parsed.outlineDark);
				setOutDark(hex);
				setOutDarkOp(opacityPct);
			}
			if (parsed?.textMainDark2) {
				const { hex, opacityPct } = parseColorValue(parsed.textMainDark2);
				setTxtMainDk2(hex);
				setTxtMainDk2Op(opacityPct);
			}
			if (parsed?.textMainMain2) {
				const { hex, opacityPct } = parseColorValue(parsed.textMainMain2);
				setTxtMainMn2(hex);
				setTxtMainMn2Op(opacityPct);
			}
			if (parsed?.textSecondaryMain) {
				const { hex, opacityPct } = parseColorValue(parsed.textSecondaryMain);
				setTxtSecMain(hex);
				setTxtSecMainOp(opacityPct);
			}
			if (parsed?.fontFamily) setFontFamily(parsed.fontFamily);
		} catch {
			// Keep current state if parse fails
		}
	}, []);

	const buildJson = useCallback(
		(state: ThemeEmitState, onChange: (value: string) => void) => {
			const obj: Record<string, string> = {
				primaryColor: state.primary,
				secondaryColor: state.secondary,
			};
			// Emit tertiary only when it overrides the documented default (#7034E7);
			// the theme pipeline supplies that default when tertiaryColor is absent.
			if (state.tertiary && state.tertiary !== "#7034E7")
				obj.tertiaryColor = state.tertiary;
			// Emit surfaceTint only when overriding the documented default (#DFF5FF);
			// the theme pipeline supplies that default when surfaceTint is absent.
			if (state.surfaceTint && state.surfaceTint !== "#DFF5FF")
				obj.surfaceTint = state.surfaceTint;
			if (state.surface && state.surface !== "#F7F4F9")
				obj.surfaceColor = state.surface;
			const surfLightVal = buildColorValue(state.surfLight, state.surfLightOp);
			if (surfLightVal !== "#FFFFFF") obj.surfaceLight = surfLightVal;
			const surfDarkVal = buildColorValue(state.surfDark, state.surfDarkOp);
			if (surfDarkVal !== "#E9D0F4") obj.surfaceDark = surfDarkVal;
			if (state.primDark)
				obj.primaryDark = buildColorValue(state.primDark, state.primDarkOp);
			if (state.secLight)
				obj.secondaryLight = buildColorValue(state.secLight, state.secLightOp);
			if (state.outMain)
				obj.outlineMain = buildColorValue(state.outMain, state.outMainOp);
			if (state.outDark)
				obj.outlineDark = buildColorValue(state.outDark, state.outDarkOp);
			if (state.txtMainDk2)
				obj.textMainDark2 = buildColorValue(
					state.txtMainDk2,
					state.txtMainDk2Op,
				);
			if (state.txtMainMn2)
				obj.textMainMain2 = buildColorValue(
					state.txtMainMn2,
					state.txtMainMn2Op,
				);
			if (state.txtSecMain)
				obj.textSecondaryMain = buildColorValue(
					state.txtSecMain,
					state.txtSecMainOp,
				);
			if (state.font && state.font !== "Roboto") obj.fontFamily = state.font;
			onChange(JSON.stringify(obj));
		},
		[],
	);

	return (
		<Controller
			name="default"
			control={control}
			rules={{ required: "Theme colors are required" }}
			render={({ field: { onChange } }) => {
				// Sync form value on mount so validation passes
				useEffect(() => {
					buildJson(
						{
							primary: primaryColor,
							secondary: secondaryColor,
							tertiary: tertiaryColor,
							surfaceTint,
							surface: surfaceColor,
							surfLight: surfaceLight,
							surfLightOp: surfaceLightOpacity,
							surfDark: surfaceDark,
							surfDarkOp: surfaceDarkOpacity,
							primDark,
							primDarkOp,
							secLight,
							secLightOp,
							outMain,
							outMainOp,
							outDark,
							outDarkOp,
							txtMainDk2,
							txtMainDk2Op,
							txtMainMn2,
							txtMainMn2Op,
							txtSecMain,
							txtSecMainOp,
							font: fontFamily,
						},
						onChange,
					);
				}, []);

				const emit = (overrides: Partial<ThemeEmitState>) => {
					buildJson(
						{
							primary: primaryColor,
							secondary: secondaryColor,
							tertiary: tertiaryColor,
							surfaceTint,
							surface: surfaceColor,
							surfLight: surfaceLight,
							surfLightOp: surfaceLightOpacity,
							surfDark: surfaceDark,
							surfDarkOp: surfaceDarkOpacity,
							primDark,
							primDarkOp,
							secLight,
							secLightOp,
							outMain,
							outMainOp,
							outDark,
							outDarkOp,
							txtMainDk2,
							txtMainDk2Op,
							txtMainMn2,
							txtMainMn2Op,
							txtSecMain,
							txtSecMainOp,
							font: fontFamily,
							...overrides,
						},
						onChange,
					);
				};

				return (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 2 }}>
							Theme Colors & Font
						</Typography>
						<Grid container spacing={2}>
							{/* Primary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Primary Color"
									value={primaryColor}
									placeholder="#276048"
									onChange={(val) => {
										setPrimaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ primary: val });
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Buttons, links, active states
								</Typography>
							</Grid>
							{/* Secondary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Secondary Color"
									value={secondaryColor}
									placeholder="#70A815"
									onChange={(val) => {
										setSecondaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ secondary: val });
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Secondary actions, badges, links
								</Typography>
							</Grid>
							{/* Tertiary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Tertiary Color"
									value={tertiaryColor}
									placeholder="#7034E7"
									onChange={(val) => {
										setTertiaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ tertiary: val });
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Accent: active tab, links, chart line
								</Typography>
							</Grid>
							{/* Surface Tint */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Surface Tint"
									value={surfaceTint}
									placeholder="#DFF5FF"
									onChange={(val) => {
										setSurfaceTint(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ surfaceTint: val });
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Tinted surfaces: table headers, tint panels
								</Typography>
							</Grid>
							{/* Surface Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Surface Color"
									value={surfaceColor}
									placeholder="#DEE7E7"
									onChange={(val) => {
										setSurfaceColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surface: val });
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Page backgrounds, card backgrounds
								</Typography>
							</Grid>
							{/* Surface Light */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Surface Light"
									hexValue={surfaceLight}
									opacityPct={surfaceLightOpacity}
									placeholder="#FFFFFF"
									caption="Dialog headers, buttons text, menu items"
									onHexChange={(val) => {
										setSurfaceLight(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surfLight: val });
									}}
									onOpacityChange={(op) => {
										setSurfaceLightOpacity(op);
										emit({ surfLightOp: op });
									}}
								/>
							</Grid>
							{/* Surface Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Surface Dark"
									hexValue={surfaceDark}
									opacityPct={surfaceDarkOpacity}
									placeholder="#E9D0F4"
									caption="Dividers, hover overlays, tinted panels"
									onHexChange={(val) => {
										setSurfaceDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surfDark: val });
									}}
									onOpacityChange={(op) => {
										setSurfaceDarkOpacity(op);
										emit({ surfDarkOp: op });
									}}
								/>
							</Grid>
							{/* Primary Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Primary Dark"
									hexValue={primDark}
									opacityPct={primDarkOp}
									placeholder="#52197B (auto)"
									caption="Dialog title bars, headers"
									onHexChange={(val) => {
										setPrimDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ primDark: val });
									}}
									onOpacityChange={(op) => {
										setPrimDarkOp(op);
										if (primDark) emit({ primDarkOp: op });
									}}
								/>
							</Grid>
							{/* Secondary Light */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Secondary Light"
									hexValue={secLight}
									opacityPct={secLightOp}
									placeholder="#E7EFB8 (auto)"
									caption="Status chips, report labels"
									onHexChange={(val) => {
										setSecLight(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ secLight: val });
									}}
									onOpacityChange={(op) => {
										setSecLightOp(op);
										if (secLight) emit({ secLightOp: op });
									}}
								/>
							</Grid>
							{/* Outline Main */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Outline Main"
									hexValue={outMain}
									opacityPct={outMainOp}
									placeholder="#D9D1DD (auto)"
									caption="Input borders, dialog dividers"
									onHexChange={(val) => {
										setOutMain(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ outMain: val });
									}}
									onOpacityChange={(op) => {
										setOutMainOp(op);
										if (outMain) emit({ outMainOp: op });
									}}
								/>
							</Grid>
							{/* Outline Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Outline Dark"
									hexValue={outDark}
									opacityPct={outDarkOp}
									placeholder="#ADA6B3 (auto)"
									caption="Borders, dividers, action menus"
									onHexChange={(val) => {
										setOutDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ outDark: val });
									}}
									onOpacityChange={(op) => {
										setOutDarkOp(op);
										if (outDark) emit({ outDarkOp: op });
									}}
								/>
							</Grid>
							{/* Text Main Dark2 */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Main Dark"
									hexValue={txtMainDk2}
									opacityPct={txtMainDk2Op}
									placeholder="#1F2026 (auto)"
									caption="Body text, labels, card content"
									onHexChange={(val) => {
										setTxtMainDk2(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtMainDk2: val });
									}}
									onOpacityChange={(op) => {
										setTxtMainDk2Op(op);
										if (txtMainDk2) emit({ txtMainDk2Op: op });
									}}
								/>
							</Grid>
							{/* Text Main Main2 */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Main Secondary"
									hexValue={txtMainMn2}
									opacityPct={txtMainMn2Op}
									placeholder="#79797D (auto)"
									caption="Secondary text, date cards, list items"
									onHexChange={(val) => {
										setTxtMainMn2(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtMainMn2: val });
									}}
									onOpacityChange={(op) => {
										setTxtMainMn2Op(op);
										if (txtMainMn2) emit({ txtMainMn2Op: op });
									}}
								/>
							</Grid>
							{/* Text Secondary Main */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Secondary"
									hexValue={txtSecMain}
									opacityPct={txtSecMainOp}
									placeholder="#79797D (auto)"
									caption="Muted labels, form hints"
									onHexChange={(val) => {
										setTxtSecMain(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtSecMain: val });
									}}
									onOpacityChange={(op) => {
										setTxtSecMainOp(op);
										if (txtSecMain) emit({ txtSecMainOp: op });
									}}
								/>
							</Grid>
							{/* Font Family */}
							<Grid item xs={12} sm={6}>
								<TextField
									label="Font Family"
									value={fontFamily}
									onChange={(e) => {
										const val = e.target.value;
										setFontFamily(val);
										emit({ font: val });
									}}
									size="small"
									fullWidth
									placeholder="Roboto"
									helperText="Regular, Medium, Semibold, Bold weights"
								/>
							</Grid>
						</Grid>

						{/* Preview swatches */}
						<Typography
							variant="caption"
							sx={{ mt: 2, mb: 1, display: "block" }}
						>
							Preview
						</Typography>
						<Box display="flex" gap={1.5} flexWrap="wrap">
							{[
								{ color: primaryColor, label: "Primary" },
								{ color: secondaryColor, label: "Secondary" },
								{ color: surfaceColor, label: "Surface" },
								{
									color: buildColorValue(surfaceLight, surfaceLightOpacity),
									label: "Surf. Light",
								},
								{
									color: buildColorValue(surfaceDark, surfaceDarkOpacity),
									label: "Surf. Dark",
								},
								...(primDark
									? [
											{
												color: buildColorValue(primDark, primDarkOp),
												label: "Prim. Dark",
											},
										]
									: []),
								...(secLight
									? [
											{
												color: buildColorValue(secLight, secLightOp),
												label: "Sec. Light",
											},
										]
									: []),
								...(outMain
									? [
											{
												color: buildColorValue(outMain, outMainOp),
												label: "Outline",
											},
										]
									: []),
								...(txtMainDk2
									? [
											{
												color: buildColorValue(txtMainDk2, txtMainDk2Op),
												label: "Txt Dark",
											},
										]
									: []),
							].map(({ color, label }) => (
								<Box
									key={label}
									sx={{
										width: 90,
										height: 44,
										backgroundColor: color,
										borderRadius: 1,
										border: "1px solid #ccc",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Typography
										variant="caption"
										sx={{
											color: getSwatchTextColor(
												color.startsWith("rgba") ? "#888888" : color,
											),
											fontFamily: fontFamily || "Roboto",
											fontWeight: 600,
										}}
									>
										{label}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				);
			}}
		/>
	);
}

/**
 * Editor sub-component for the beneficiaryTerms setting.
 * Shows two text fields for singular and plural terms.
 */
function BeneficiaryTermsPicker({
	control,
	watch,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
}) {
	const currentValue = watch("default");
	const [singularTerm, setSingularTerm] = useState("Beneficiary");
	const [pluralTerm, setPluralTerm] = useState("Beneficiaries");

	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (parsed?.singular) setSingularTerm(parsed.singular);
			if (parsed?.plural) setPluralTerm(parsed.plural);
		} catch {
			// Keep current state if parse fails
		}
	}, []);

	const buildJson = useCallback(
		(singular: string, plural: string, onChange: (value: string) => void) => {
			onChange(JSON.stringify({ singular, plural }));
		},
		[],
	);

	return (
		<Controller
			name="default"
			control={control}
			rules={{ required: "Beneficiary terms are required" }}
			render={({ field: { onChange } }) => {
				// Sync form value on mount so validation passes
				useEffect(() => {
					buildJson(singularTerm, pluralTerm, onChange);
				}, []);

				return (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 2 }}>
							Beneficiary Terminology
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Singular Term"
									value={singularTerm}
									onChange={(e) => {
										const val = e.target.value;
										setSingularTerm(val);
										buildJson(val, pluralTerm, onChange);
									}}
									fullWidth
									placeholder="Beneficiary"
									helperText='e.g., "Participant", "Client", "Member"'
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Plural Term"
									value={pluralTerm}
									onChange={(e) => {
										const val = e.target.value;
										setPluralTerm(val);
										buildJson(singularTerm, val, onChange);
									}}
									fullWidth
									placeholder="Beneficiaries"
									helperText='e.g., "Participants", "Clients", "Members"'
								/>
							</Grid>
						</Grid>
					</Box>
				);
			}}
		/>
	);
}

/**
 * Editor sub-component for the certificateConfig setting.
 * Shows fields for logos, colors, text, toggles matching the certificate template.
 */
function CertificateConfigPicker({
	control,
	watch,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
}) {
	const currentValue = watch("default");

	const [accountLogoUrl, setAccountLogoUrl] = useState("");
	const [accountLogoWidth, setAccountLogoWidth] = useState(80);
	const [accountLogoHeight, setAccountLogoHeight] = useState(80);
	const [secondaryLogoUrl, setSecondaryLogoUrl] = useState("");
	const [secondaryLogoWidth, setSecondaryLogoWidth] = useState(60);
	const [secondaryLogoHeight, setSecondaryLogoHeight] = useState(60);
	const [titleText, setTitleText] = useState("Certificate of Completion");
	const [titleColor, setTitleColor] = useState("#1a1a1a");
	const [titleFontSize, setTitleFontSize] = useState(48);
	const [primaryTextColor, setPrimaryTextColor] = useState("#333333");
	const [secondaryTextColor, setSecondaryTextColor] = useState("#8B0000");
	const [congratulationsText, setCongratulationsText] = useState(
		"Congratulations to:",
	);
	const [completionMessageTemplate, setCompletionMessageTemplate] = useState(
		"For Completing the {orgName} {programName}!",
	);
	const [coordinatorLabel, setCoordinatorLabel] = useState(
		"CM Coordinator/Supervisor",
	);
	const [dateLabel, setDateLabel] = useState("Date of Completion");
	const [showCoordinatorName, setShowCoordinatorName] = useState(true);
	const [coordinatorTitleOverride, setCoordinatorTitleOverride] = useState("");
	const [showTrophyIcon, setShowTrophyIcon] = useState(true);
	const [showBadgesIcon, setShowBadgesIcon] = useState(true);
	const [borderStyle, setBorderStyle] = useState("gold-ornamental");
	const [borderColor, setBorderColor] = useState("#DAA520");
	const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
	const [backgroundImage, setBackgroundImage] = useState("");

	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (!parsed) return;
			if (parsed.accountLogo?.url) setAccountLogoUrl(parsed.accountLogo.url);
			if (parsed.accountLogo?.width)
				setAccountLogoWidth(parsed.accountLogo.width);
			if (parsed.accountLogo?.height)
				setAccountLogoHeight(parsed.accountLogo.height);
			if (parsed.secondaryLogo?.url)
				setSecondaryLogoUrl(parsed.secondaryLogo.url);
			if (parsed.secondaryLogo?.width)
				setSecondaryLogoWidth(parsed.secondaryLogo.width);
			if (parsed.secondaryLogo?.height)
				setSecondaryLogoHeight(parsed.secondaryLogo.height);
			if (parsed.titleText) setTitleText(parsed.titleText);
			if (parsed.titleColor) setTitleColor(parsed.titleColor);
			if (parsed.titleFontSize) setTitleFontSize(parsed.titleFontSize);
			if (parsed.primaryTextColor) setPrimaryTextColor(parsed.primaryTextColor);
			if (parsed.secondaryTextColor)
				setSecondaryTextColor(parsed.secondaryTextColor);
			if (parsed.congratulationsText)
				setCongratulationsText(parsed.congratulationsText);
			if (parsed.completionMessageTemplate)
				setCompletionMessageTemplate(parsed.completionMessageTemplate);
			if (parsed.footerLabels?.coordinator)
				setCoordinatorLabel(parsed.footerLabels.coordinator);
			if (parsed.footerLabels?.date) setDateLabel(parsed.footerLabels.date);
			if (parsed.showCoordinatorName !== undefined)
				setShowCoordinatorName(parsed.showCoordinatorName);
			if (parsed.coordinatorTitleOverride)
				setCoordinatorTitleOverride(parsed.coordinatorTitleOverride);
			if (parsed.showTrophyIcon !== undefined)
				setShowTrophyIcon(parsed.showTrophyIcon);
			if (parsed.showBadgesIcon !== undefined)
				setShowBadgesIcon(parsed.showBadgesIcon);
			if (parsed.borderStyle) setBorderStyle(parsed.borderStyle);
			if (parsed.borderColor) setBorderColor(parsed.borderColor);
			if (parsed.backgroundColor) setBackgroundColor(parsed.backgroundColor);
			if (parsed.backgroundImage !== undefined)
				setBackgroundImage(parsed.backgroundImage);
		} catch {
			// Keep defaults
		}
	}, []);

	const buildJson = useCallback(
		(overrides: Record<string, unknown>, onChange: (value: string) => void) => {
			const obj = {
				accountLogo: {
					url: overrides.accountLogoUrl ?? accountLogoUrl,
					width: overrides.accountLogoWidth ?? accountLogoWidth,
					height: overrides.accountLogoHeight ?? accountLogoHeight,
				},
				secondaryLogo: {
					url: overrides.secondaryLogoUrl ?? secondaryLogoUrl,
					width: overrides.secondaryLogoWidth ?? secondaryLogoWidth,
					height: overrides.secondaryLogoHeight ?? secondaryLogoHeight,
				},
				titleText: overrides.titleText ?? titleText,
				titleColor: overrides.titleColor ?? titleColor,
				titleFontSize: overrides.titleFontSize ?? titleFontSize,
				primaryTextColor: overrides.primaryTextColor ?? primaryTextColor,
				secondaryTextColor: overrides.secondaryTextColor ?? secondaryTextColor,
				congratulationsText:
					overrides.congratulationsText ?? congratulationsText,
				completionMessageTemplate:
					overrides.completionMessageTemplate ?? completionMessageTemplate,
				footerLabels: {
					coordinator: overrides.coordinatorLabel ?? coordinatorLabel,
					date: overrides.dateLabel ?? dateLabel,
				},
				showCoordinatorName:
					overrides.showCoordinatorName ?? showCoordinatorName,
				coordinatorTitleOverride:
					overrides.coordinatorTitleOverride ?? coordinatorTitleOverride,
				showTrophyIcon: overrides.showTrophyIcon ?? showTrophyIcon,
				showBadgesIcon: overrides.showBadgesIcon ?? showBadgesIcon,
				borderStyle: overrides.borderStyle ?? borderStyle,
				borderColor: overrides.borderColor ?? borderColor,
				backgroundColor: overrides.backgroundColor ?? backgroundColor,
				backgroundImage: overrides.backgroundImage ?? backgroundImage,
			};
			onChange(JSON.stringify(obj));
		},
		[
			accountLogoUrl,
			accountLogoWidth,
			accountLogoHeight,
			secondaryLogoUrl,
			secondaryLogoWidth,
			secondaryLogoHeight,
			titleText,
			titleColor,
			titleFontSize,
			primaryTextColor,
			secondaryTextColor,
			congratulationsText,
			completionMessageTemplate,
			coordinatorLabel,
			dateLabel,
			showCoordinatorName,
			coordinatorTitleOverride,
			showTrophyIcon,
			showBadgesIcon,
			borderStyle,
			borderColor,
			backgroundColor,
			backgroundImage,
		],
	);

	return (
		<Controller
			name="default"
			control={control}
			rules={{ required: "Certificate config is required" }}
			render={({ field: { onChange } }) => {
				useEffect(() => {
					buildJson({}, onChange);
				}, []);

				return (
					<Box>
						{/* Logos Section */}
						<Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
							Logos
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Account Logo URL"
									value={accountLogoUrl}
									onChange={(e) => {
										setAccountLogoUrl(e.target.value);
										buildJson({ accountLogoUrl: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="PNG or JPEG. Leave empty to show organization name only"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Width"
									type="number"
									value={accountLogoWidth}
									onChange={(e) => {
										const val = Number(e.target.value);
										setAccountLogoWidth(val);
										buildJson({ accountLogoWidth: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Height"
									type="number"
									value={accountLogoHeight}
									onChange={(e) => {
										const val = Number(e.target.value);
										setAccountLogoHeight(val);
										buildJson({ accountLogoHeight: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Secondary Logo URL (optional)"
									value={secondaryLogoUrl}
									onChange={(e) => {
										setSecondaryLogoUrl(e.target.value);
										buildJson({ secondaryLogoUrl: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="Optional secondary logo (e.g., partner organization)"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Width"
									type="number"
									value={secondaryLogoWidth}
									onChange={(e) => {
										const val = Number(e.target.value);
										setSecondaryLogoWidth(val);
										buildJson({ secondaryLogoWidth: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Height"
									type="number"
									value={secondaryLogoHeight}
									onChange={(e) => {
										const val = Number(e.target.value);
										setSecondaryLogoHeight(val);
										buildJson({ secondaryLogoHeight: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							{(accountLogoUrl || secondaryLogoUrl) && (
								<Grid item xs={12}>
									<Box display="flex" gap={2} alignItems="center">
										{accountLogoUrl && (
											<Box>
												<Typography variant="caption">Account Logo</Typography>
												<img
													src={accountLogoUrl}
													alt="Account logo preview"
													style={{
														maxWidth: accountLogoWidth,
														maxHeight: accountLogoHeight,
														display: "block",
														border: "1px solid #eee",
														borderRadius: 4,
													}}
													onError={(e) => {
														(e.target as HTMLImageElement).style.display =
															"none";
													}}
												/>
											</Box>
										)}
										{secondaryLogoUrl && (
											<Box>
												<Typography variant="caption">
													Secondary Logo
												</Typography>
												<img
													src={secondaryLogoUrl}
													alt="Secondary logo preview"
													style={{
														maxWidth: secondaryLogoWidth,
														maxHeight: secondaryLogoHeight,
														display: "block",
														border: "1px solid #eee",
														borderRadius: 4,
													}}
													onError={(e) => {
														(e.target as HTMLImageElement).style.display =
															"none";
													}}
												/>
											</Box>
										)}
									</Box>
								</Grid>
							)}
						</Grid>

						{/* Title & Text Section */}
						<Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
							Title & Text
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Title Text"
									value={titleText}
									onChange={(e) => {
										setTitleText(e.target.value);
										buildJson({ titleText: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<TextField
									label="Title Font Size"
									type="number"
									value={titleFontSize}
									onChange={(e) => {
										const val = Number(e.target.value);
										setTitleFontSize(val);
										buildJson({ titleFontSize: val }, onChange);
									}}
									fullWidth
									size="small"
									inputProps={{ min: 24, max: 72 }}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Congratulations Text"
									value={congratulationsText}
									onChange={(e) => {
										setCongratulationsText(e.target.value);
										buildJson(
											{ congratulationsText: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Completion Message Template"
									value={completionMessageTemplate}
									onChange={(e) => {
										setCompletionMessageTemplate(e.target.value);
										buildJson(
											{ completionMessageTemplate: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
									helperText="Variables: {orgName}, {programName}"
								/>
							</Grid>
						</Grid>

						{/* Colors Section */}
						<Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
							Colors
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Title Color"
									value={titleColor}
									placeholder="#1a1a1a"
									onChange={(val) => {
										setTitleColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ titleColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Primary Text Color"
									value={primaryTextColor}
									placeholder="#333333"
									onChange={(val) => {
										setPrimaryTextColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ primaryTextColor: val }, onChange);
										}
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Body text, beneficiary name
								</Typography>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Secondary Text Color"
									value={secondaryTextColor}
									placeholder="#8B0000"
									onChange={(val) => {
										setSecondaryTextColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ secondaryTextColor: val }, onChange);
										}
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									Footer labels (Coordinator, Date)
								</Typography>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Border Color"
									value={borderColor}
									placeholder="#DAA520"
									onChange={(val) => {
										setBorderColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ borderColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Background Color"
									value={backgroundColor}
									placeholder="#FFFFFF"
									onChange={(val) => {
										setBackgroundColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ backgroundColor: val }, onChange);
										}
									}}
								/>
							</Grid>
						</Grid>

						{/* Footer Section */}
						<Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
							Footer
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Coordinator Label"
									value={coordinatorLabel}
									onChange={(e) => {
										setCoordinatorLabel(e.target.value);
										buildJson({ coordinatorLabel: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Date Label"
									value={dateLabel}
									onChange={(e) => {
										setDateLabel(e.target.value);
										buildJson({ dateLabel: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Coordinator Title Override (optional)"
									value={coordinatorTitleOverride}
									onChange={(e) => {
										setCoordinatorTitleOverride(e.target.value);
										buildJson(
											{ coordinatorTitleOverride: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
									helperText="Override coordinator title text"
								/>
							</Grid>
						</Grid>

						{/* Display Options */}
						<Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
							Display Options
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<FormControlLabel
									control={
										<Switch
											checked={showCoordinatorName}
											onChange={(e) => {
												setShowCoordinatorName(e.target.checked);
												buildJson(
													{ showCoordinatorName: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Coordinator Name"
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<FormControlLabel
									control={
										<Switch
											checked={showTrophyIcon}
											onChange={(e) => {
												setShowTrophyIcon(e.target.checked);
												buildJson(
													{ showTrophyIcon: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Trophy Icon"
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<FormControlLabel
									control={
										<Switch
											checked={showBadgesIcon}
											onChange={(e) => {
												setShowBadgesIcon(e.target.checked);
												buildJson(
													{ showBadgesIcon: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Badges Icon"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel>Border Style</InputLabel>
									<Select
										value={borderStyle}
										label="Border Style"
										onChange={(e) => {
											setBorderStyle(e.target.value);
											buildJson({ borderStyle: e.target.value }, onChange);
										}}
									>
										<MenuItem value="gold-ornamental">Gold Ornamental</MenuItem>
										<MenuItem value="simple">Simple</MenuItem>
										<MenuItem value="none">None</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Background Image URL (optional)"
									value={backgroundImage}
									onChange={(e) => {
										setBackgroundImage(e.target.value);
										buildJson({ backgroundImage: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="Optional custom background image URL"
								/>
							</Grid>
						</Grid>

						{/* Preview */}
						<Typography
							variant="caption"
							sx={{ mt: 3, mb: 1, display: "block" }}
						>
							Color Preview
						</Typography>
						<Box display="flex" gap={1.5} flexWrap="wrap">
							{[
								{ color: titleColor, label: "Title" },
								{ color: primaryTextColor, label: "Primary" },
								{ color: secondaryTextColor, label: "Secondary" },
								{ color: borderColor, label: "Border" },
								{ color: backgroundColor, label: "Background" },
							].map(({ color, label }) => (
								<Box
									key={label}
									sx={{
										width: 80,
										height: 40,
										backgroundColor: color,
										borderRadius: 1,
										border: "1px solid #ccc",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Typography
										variant="caption"
										sx={{
											color: getSwatchTextColor(color),
											fontWeight: 600,
											fontSize: "0.65rem",
										}}
									>
										{label}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				);
			}}
		/>
	);
}

export default SettingsModal;
